import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { useState, useEffect } from 'react';
import { tap, map, filter, distinctUntilKeyChanged } from 'rxjs/operators';

import { getTimeslots, getByPostalCode, getTimeslotsForTakeAwayShop } from '../../api/Shops';
import DeliveryMode from '../../store/deliveryMode';
import { Order } from '../../store/order';
import { Shop } from '../../store/shop';
import User from '../../store/user';
import { formatUnixDate } from '../../utils/dayjs';
import OrderAlertModal from '../OrderAlertModal/OrderAlertModal';

import TimeSlotsMobile from './TimeSlotsMobile';
import styles from './TimeSlotsToDeliver.module.scss';

export default function TimeSlotsToDeliver({ setPostalCodeUnsupported }) {
  const [order, setOrder] = useState();
  const [user, setUser] = useState();
  const [availableTimeSlots, setAvailableTimeSlots] = useState('');
  const [selectOptionsDays, setSelectOptionsDays] = useState('');
  const [selectOptionsSlotsByDay, setSelectOptionsSlotsByDay] = useState('');
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [isModalOpen, setOpenModal] = useState(false);
  const [alertText, setAlertText] = useState('');
  const modalLabel = 'Διαθεθιμότητα υπηρεσίας';

  const RESET_CHOSEN_TIMESLOTS = {
    timeSlotEnd: undefined,
    timeSlotStart: undefined,
  };

  function timeSlotsToDays(timeSlots) {
    if (!timeSlots || timeSlots.length === 0) {
      return [];
    }

    const group = timeSlots.reduce((accumulator, currentValue) => {
      const day = formatUnixDate(currentValue.timeSlotStart, 'DD-MM');
      accumulator[day] = [...(accumulator[day] || []), currentValue];
      return accumulator;
    }, {});

    return Object.values(group);
  }

  function titleDayMsg(i, start) {
    return formatUnixDate(start, 'dddd, DD MMM YYYY');
  }

  function createSelectOptions(slotsEasy, slotsExpress, slotsTakeAway, _service) {
    if (_service === 'easy') {
      if (slotsEasy.length) {
        const _selectOptionsDays = [];
        const selectOptionsSlots = [];
        slotsEasy.forEach((day, index) => {
          _selectOptionsDays.push({
            value: day,
            label: titleDayMsg(index, day[0].timeSlotStart),
            index,
          });
          day.forEach(slot => {
            selectOptionsSlots.push({
              value: slot,
              label: `${formatUnixDate(slot.timeSlotStart, 'HH:mm')} - ${formatUnixDate(slot.timeSlotEnd, 'HH:mm')}`,
              isDisabled: !slot.isAvailable,
              timeSlotStart: slot.timeSlotStart,
            });
          });
        });
        const _selectOptionsSlotsByDay = timeSlotsToDays(selectOptionsSlots);
        setSelectOptionsDays(_selectOptionsDays);
        setSelectOptionsSlotsByDay(_selectOptionsSlotsByDay);
        return;
      }
      setOpenModal(true);
      setAlertText('Η διεύθυνση που επέλεξες δυστυχώς δεν εξυπηρετείται ακόμα για αυτή την υπηρεσία.');
      return;
    }
    if (_service === 'express') {
      if (slotsExpress.length) {
        const _selectOptionsDays = [];
        const selectOptionsSlots = [];
        slotsExpress.forEach((day, index) => {
          _selectOptionsDays.push({
            value: day,
            label: titleDayMsg(index, day[0].timeSlotStart),
            index,
          });
          day.forEach(slot => {
            selectOptionsSlots.push({
              value: slot,
              label: `${formatUnixDate(slot.timeSlotStart, 'HH:mm')} - ${formatUnixDate(slot.timeSlotEnd, 'HH:mm')}`,
              isDisabled: !slot.isAvailable,
              timeSlotStart: slot.timeSlotStart,
            });
          });
        });
        const _selectOptionsSlotsByDay = timeSlotsToDays(selectOptionsSlots);
        setSelectOptionsDays(_selectOptionsDays);
        setSelectOptionsSlotsByDay(_selectOptionsSlotsByDay);
        return;
      }
      setOpenModal(true);
      setAlertText('Η διεύθυνση που επέλεξες δυστυχώς δεν εξυπηρετείται ακόμα για αυτή την υπηρεσία.');
      return;
    }
    if (_service === 'takeAway') {
      if (slotsTakeAway.length) {
        const _selectOptionsDays = [];
        const selectOptionsSlots = [];
        slotsTakeAway.forEach((day, index) => {
          _selectOptionsDays.push({
            value: day,
            label: titleDayMsg(index, day[0].timeSlotStart),
            index,
          });
          day.forEach(slot => {
            selectOptionsSlots.push({
              value: slot,
              label: `${formatUnixDate(slot.timeSlotStart, 'HH:mm')} - ${formatUnixDate(slot.timeSlotEnd, 'HH:mm')}`,
              isDisabled: !slot.isAvailable,
              timeSlotStart: slot.timeSlotStart,
            });
          });
        });
        const _selectOptionsSlotsByDay = timeSlotsToDays(selectOptionsSlots);
        setSelectOptionsDays(_selectOptionsDays);
        setSelectOptionsSlotsByDay(_selectOptionsSlotsByDay);
      }
    }
  }

  useEffect(() => {
    const user$ = User.subscribe(setUser);
    return () => {
      user$.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const shop$ = Shop.subscribe(async shop => {
      if (!shop?.takeAwayShop) return;

      const selectedService = DeliveryMode.getValue();
      if (selectedService !== 'takeAway') return;

      const resTakeAway = await getTimeslotsForTakeAwayShop(shop?.takeAwayShop?._id);
      if (resTakeAway?.timeslots?.length) {
        Order.setShopId(resTakeAway.timeslots[0].shopId);
        setPostalCodeUnsupported(false);
      } else {
        setPostalCodeUnsupported(true);
      }
      Order.deleteProperty(['addressObject']);
      const daysTakeAway = timeSlotsToDays(resTakeAway.timeslots);
      setAvailableTimeSlots(daysTakeAway);
      createSelectOptions(null, null, daysTakeAway, selectedService);
    });

    const order$ = Order.subject
      .pipe(
        tap(_order => setOrder(_order)),
        filter(_order => _order?.addressObject),
        map(({ addressObject }) => addressObject),
        distinctUntilKeyChanged('postalCode')
      )
      .subscribe(async ({ postalCode }) => {
        const selectedService = DeliveryMode.getValue();
        if (selectedService === 'takeAway') return;
        Order.setTimeslot(RESET_CHOSEN_TIMESLOTS);
        if (selectedService === 'easy') {
          const { shops: easyShops } = await getByPostalCode(postalCode);
          const timeSlots = easyShops?.[0]?.timeSlots ?? [];
          if (timeSlots.length) {
            Order.setShopId(easyShops[0]._id);
            setPostalCodeUnsupported(false);
          } else {
            setPostalCodeUnsupported(true);
          }
          const daysEasy = timeSlotsToDays(timeSlots);
          setAvailableTimeSlots(daysEasy);
          createSelectOptions(daysEasy, null, null, selectedService);
          return;
        }
        if (selectedService === 'express') {
          const resExpress = await getTimeslots(postalCode);
          if (resExpress?.timeslots?.length) {
            Order.setShopId(resExpress.timeslots[0].shopId);
            setPostalCodeUnsupported(false);
          } else {
            setPostalCodeUnsupported(true);
          }
          const daysExpress = timeSlotsToDays(resExpress.timeslots);
          setAvailableTimeSlots(daysExpress);
          createSelectOptions(null, daysExpress, null, selectedService);
        }
      });

    return () => {
      shop$.unsubscribe();
      order$.unsubscribe();
    };
  }, [user]);

  function onClickSelectTimeSlot(timeslot) {
    Order.setTimeslot(timeslot);
  }

  function onChangeSelectDay(day) {
    setSelectedDay(day);
    if (day && day.label !== selectedDay.label && selectedTime && selectOptionsSlotsByDay) {
      const newTimeslot = selectOptionsSlotsByDay[day.index]?.find(_timeslot => _timeslot.label === selectedTime.label);
      if (newTimeslot?.isDisabled) {
        setSelectedTime(null);
        Order.setTimeslot(RESET_CHOSEN_TIMESLOTS);
      }
    }
  }

  function onChangeSelectTimeslot(timeslot) {
    setSelectedTime(timeslot);
    Order.setTimeslot(timeslot.value);
  }
  if (!order) return null;
  return (
    <section className={styles.section}>
      <h2 className={styles.timeSlotsTitle}>ΩΡΑ ΠΑΡΑΔΟΣΗΣ</h2>
      {!isEmpty(availableTimeSlots[0]) && (
        <>
          <p className={styles.instructions}>
            Επίλεξε τη ημέρα & ώρα που θέλεις να παραδοθούν οι αγορές σου από το κατάστημα.
          </p>
          <div role="group" className={cn(styles.selectionDayContainer, {})}>
            {availableTimeSlots.map((timeSlot, index) => (
              <div className={cn(styles.selectionDay)} key={timeSlot[0].timeSlotStart}>
                <h4 className={styles.selectionDayHeader}>{titleDayMsg(index, timeSlot[0].timeSlotStart)}</h4>
                {timeSlot.map(slot => (
                  <div key={slot.timeSlotStart}>
                    <button
                      name="timeSlot"
                      role="radio"
                      aria-checked={order.timeSlotStart === slot.timeSlotStart}
                      id={slot.timeSlotStart}
                      value={slot.timeSlotStart}
                      className={cn(styles.timeSlotInput, {
                        [styles.disabledTimeslot]: !slot.isAvailable,
                        [styles.selectedTimeslot]: order.timeSlotStart === slot.timeSlotStart,
                      })}
                      onClick={() => onClickSelectTimeSlot(slot)}
                      disabled={!slot.isAvailable}
                    >
                      <span
                        className={styles.timeSlotLabel}
                        data-cy={slot.isAvailable ? 'timeSlot-radio-active' : 'timeSlot-radio-disabled'}
                      >
                        {`${formatUnixDate(slot.timeSlotStart, 'HH:mm')} -
                      ${formatUnixDate(slot.timeSlotEnd, 'HH:mm')}`}
                      </span>
                    </button>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className={styles.selectContainer}>
            <TimeSlotsMobile
              selectedDay={selectedDay}
              onChangeSelectDay={onChangeSelectDay}
              selectOptionsDays={selectOptionsDays}
              selectedTime={selectedTime}
              onChangeSelectTimeslot={onChangeSelectTimeslot}
              selectOptionsSlotsByDay={selectOptionsSlotsByDay[selectedDay?.index]}
            />
          </div>
        </>
      )}
      {isModalOpen && (
        <OrderAlertModal
          alertText={alertText}
          isModalOpen={isModalOpen}
          modalLabel={modalLabel}
          setOpenModal={setOpenModal}
          callback={() => setOpenModal(false)}
        />
      )}
    </section>
  );
}
